import { useState } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useLanguage } from '../contexts/LanguageContext'
import { useTestimonials } from '../hooks/useTestimonials'
import { Quote, Youtube, Instagram, Image as ImageIcon, Video, FileText, Play, MapPin, User } from 'lucide-react'

export function Testimonials() {
  const { t } = useTranslation()
  const { isRTL } = useLanguage()
  const { testimonials, loading, error } = useTestimonials()
  const [playingVideo, setPlayingVideo] = useState<string | null>(null)

  const getTestimonialIcon = (type: string) => {
    switch (type) {
      case 'youtube':
        return <Youtube className="h-5 w-5" />
      case 'instagram':
        return <Instagram className="h-5 w-5" />
      case 'image':
        return <ImageIcon className="h-5 w-5" />
      case 'video':
        return <Video className="h-5 w-5" />
      default:
        return <FileText className="h-5 w-5" />
    }
  }

  const getYouTubeId = (url: string) => {
    // Handle YouTube Shorts URLs (youtube.com/shorts/VIDEO_ID)
    if (url.includes('/shorts/')) {
      const shortsMatch = url.match(/\/shorts\/([^#&?\/]*)/)
      if (shortsMatch && shortsMatch[1].length === 11) {
        return shortsMatch[1]
      }
    }
    // Handle regular YouTube URLs
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
    const match = url.match(regExp)
    return match && match[2].length === 11 ? match[2] : url
  }

  const getYouTubeThumbnail = (videoId: string) => {
    return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
  }

  const renderTestimonialContent = (testimonial: any) => {
    switch (testimonial.type) {
      case 'youtube':
        const videoId = getYouTubeId(testimonial.content)
        const isPlaying = playingVideo === testimonial.id
        
        if (isPlaying) {
          return (
            <div className="w-full aspect-video rounded-t-2xl overflow-hidden">
              <iframe
                src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
          )
        }
        
        return (
          <div 
            className="relative w-full aspect-video rounded-t-2xl overflow-hidden group cursor-pointer bg-gradient-to-br from-primary/20 to-primary/10"
            onClick={() => setPlayingVideo(testimonial.id)}
          >
            <img
              src={getYouTubeThumbnail(videoId)}
              alt="YouTube video thumbnail"
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
              onError={(e) => {
                // Fallback to a lower quality thumbnail if maxresdefault fails
                const target = e.target as HTMLImageElement
                target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
              }}
            />
            <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors flex items-center justify-center">
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center shadow-2xl"
              >
                <Play className="h-10 w-10 text-white ml-1" fill="white" />
              </motion.div>
            </div>
            <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-xs font-medium flex items-center gap-1">
              <Youtube className="h-3 w-3" />
              YouTube
            </div>
          </div>
        )
      
      case 'instagram':
        // Instagram embed - using basic iframe embed
        return (
          <div className="w-full">
            <blockquote
              className="instagram-media"
              data-instgrm-permalink={testimonial.content}
              data-instgrm-version="14"
              style={{
                background: '#FFF',
                border: '0',
                borderRadius: '3px',
                boxShadow: '0 0 1px 0 rgba(0,0,0,0.5), 0 1px 10px 0 rgba(0,0,0,0.15)',
                margin: '1px',
                maxWidth: '100%',
                minWidth: '326px',
                padding: '0',
                width: '99.375%'
              }}
            >
              <div style={{ padding: '16px' }}>
                <a
                  href={testimonial.content}
                  style={{
                    background: '#FFFFFF',
                    lineHeight: 0,
                    padding: '0 0',
                    textAlign: 'center',
                    textDecoration: 'none',
                    width: '100%'
                  }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View this post on Instagram
                </a>
              </div>
            </blockquote>
            <script async src="//www.instagram.com/embed.js"></script>
          </div>
        )
      
      case 'image':
        return (
          <div className="relative w-full aspect-video rounded-t-2xl overflow-hidden group">
            <img
              src={testimonial.content}
              alt={testimonial.author_name || 'Testimonial'}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
        )
      
      case 'video':
        const isVideoPlaying = playingVideo === testimonial.id
        if (isVideoPlaying) {
          return (
            <div className="w-full aspect-video rounded-t-2xl overflow-hidden">
              <video
                src={testimonial.content}
                controls
                autoPlay
                className="w-full h-full object-cover"
              >
                Your browser does not support the video tag.
              </video>
            </div>
          )
        }
        return (
          <div 
            className="relative w-full aspect-video rounded-t-2xl overflow-hidden group cursor-pointer bg-gradient-to-br from-primary/20 to-primary/10"
            onClick={() => setPlayingVideo(testimonial.id)}
          >
            <video
              src={testimonial.content}
              className="w-full h-full object-cover"
              preload="metadata"
            >
              Your browser does not support the video tag.
            </video>
            <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors flex items-center justify-center">
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="w-20 h-20 bg-primary rounded-full flex items-center justify-center shadow-2xl"
              >
                <Play className="h-10 w-10 text-white ml-1" fill="white" />
              </motion.div>
            </div>
            <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-xs font-medium flex items-center gap-1">
              <Video className="h-3 w-3" />
              Video
            </div>
          </div>
        )
      
      default: // text
        return (
          <div className="relative min-h-[200px] p-4">
            <Quote className="absolute top-2 left-2 h-12 w-12 text-primary/20" />
            <div className="text-lg text-foreground leading-relaxed relative z-10 pt-8" dir={isRTL ? 'rtl' : 'ltr'}>
              {testimonial.content}
            </div>
          </div>
        )
    }
  }

  if (loading) {
    return (
      <div className="pt-16 flex justify-center items-center min-h-screen">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading testimonials...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="pt-16 flex justify-center items-center min-h-screen">
        <div className="text-center">
          <p className="text-destructive mb-4">Error loading testimonials: {error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="pt-16 min-h-screen bg-background">
      {/* Header */}
      <section className="relative py-16 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: `linear-gradient(to right, currentColor 1px, transparent 1px),
                           linear-gradient(to bottom, currentColor 1px, transparent 1px)`,
          backgroundSize: '40px 40px',
          color: 'currentColor'
        }}></div>
        <div className="relative max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
              className="inline-block mb-4"
            >
              <Quote className="h-16 w-16 text-primary/30" />
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-4"
            >
              {t('testimonials.title', 'Testimonials')}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto"
            >
              {t('testimonials.subtitle', 'Hear from our pilgrims about their journey experiences')}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-12 bg-background">
        <div className="max-w-7xl mx-auto px-4">
          {testimonials.length === 0 ? (
            <div className="text-center py-20">
              <Quote className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-xl text-muted-foreground">
                {t('testimonials.noTestimonials', 'No testimonials available yet')}
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                  className="group bg-card border border-border/50 rounded-2xl overflow-hidden hover:border-primary/50 hover:shadow-2xl transition-all duration-300 flex flex-col h-full"
                >
                  {/* Content */}
                  <div className="relative w-full flex-shrink-0">
                    {renderTestimonialContent(testimonial)}
                    {/* Type Badge */}
                    <div className="absolute top-3 left-3 z-10">
                      <div className="flex items-center gap-1.5 bg-black/70 backdrop-blur-sm text-white px-2.5 py-1 rounded-full text-xs font-medium">
                        {getTestimonialIcon(testimonial.type)}
                        <span className="capitalize">{testimonial.type}</span>
                      </div>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="p-5 flex-1 flex flex-col">
                    {/* Author Info */}
                    {(testimonial.author_name || testimonial.author_location) && (
                      <div className="space-y-1">
                        {testimonial.author_name && (
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-primary" />
                            <p className="font-semibold text-foreground">{testimonial.author_name}</p>
                          </div>
                        )}
                        {testimonial.author_location && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <MapPin className="h-3.5 w-3.5" />
                            <span>{testimonial.author_location}</span>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Text content preview for non-media types */}
                    {testimonial.type === 'text' && (
                      <div className="mt-3 pt-3 border-t border-border/50">
                        <Quote className="h-5 w-5 text-primary/30 mb-2" />
                        <p className="text-sm text-muted-foreground line-clamp-3" dir={isRTL ? 'rtl' : 'ltr'}>
                          {testimonial.content}
                        </p>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  )
}

